import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Tag, Typography } from 'antd';

import { formatDateTime, getTaskPriorityLabel, isOverdue, PRIORITY_TONE } from '../lib/taskMeta.js';
import type { TaskKanbanCardModel } from '../lib/taskKanban.js';

const { Text } = Typography;

/** Spec D `--font-mono`: даты карточек — mono + tabular-nums. */
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

export interface TaskKanbanCardProps {
  card: TaskKanbanCardModel;
  isUpdating: boolean;
  onDragStart: (card: TaskKanbanCardModel) => void;
}

export function TaskKanbanCard({
  card,
  isUpdating,
  onDragStart,
}: TaskKanbanCardProps): ReactElement {
  useI18nSubscription();
  const overdue = isOverdue({ dueAt: card.dueAt, status: card.status });

  return (
    <article
      draggable={!isUpdating}
      data-testid={`task-kanban-card-${card.taskId}`}
      className="crm-hover-fill crm-radius-s cursor-grab transition-colors active:cursor-grabbing hover:border-[var(--ant-color-border)]"
      onDragStart={() => onDragStart(card)}
      style={{
        padding: '8px 10px',
        backgroundColor: overdue ? 'var(--ant-color-error-bg)' : 'var(--ant-color-bg-container)',
        border: `1px solid ${overdue ? 'var(--ant-color-error-border)' : 'var(--ant-color-border-secondary)'}`,
        boxShadow: 'none',
      }}
      aria-busy={isUpdating}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        {overdue ? (
          <Tag
            data-testid="task-kanban-card-overdue"
            style={{
              color: 'var(--ant-color-error)',
              background: 'var(--ant-color-error-bg)',
              borderColor: 'var(--ant-color-error-border)',
              marginInlineEnd: 0,
            }}
          >
            {i18n.t('crm.features.features.tasks.components.taskkanbancard.просрочено')}
          </Tag>
        ) : null}
        <EntityStatusChip tone={PRIORITY_TONE[card.priority]}>
          {getTaskPriorityLabel(card.priority)}
        </EntityStatusChip>
        {card.assigneeName ? (
          <Text style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)' }}>
            {card.assigneeName}
          </Text>
        ) : null}
      </div>

      <h3
        className="m-0"
        style={{ fontSize: 12.5, fontWeight: 550, lineHeight: 1.3, wordBreak: 'break-word' }}
      >
        <Link className="text-[var(--ant-color-text)] hover:underline" to={`/tasks/${card.taskId}`}>
          {card.title}
        </Link>
      </h3>

      {card.dealTitle || card.dueAt ? (
        <div
          className="mt-1.5 flex flex-wrap items-center gap-2"
          style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)' }}
        >
          {card.dealTitle ? <span className="truncate">{card.dealTitle}</span> : null}
          {card.dueAt ? (
            <span
              style={{
                fontFamily: MONO_FONT,
                fontVariantNumeric: 'tabular-nums',
                ...(overdue ? { color: 'var(--ant-color-error-text)', fontWeight: 550 } : {}),
              }}
            >
              {i18n.t('crm.features.features.tasks.components.taskkanbancard.срок')}
              {formatDateTime(card.dueAt)}
            </span>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
